import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { CtaButton } from './CtaButton';
const RECIPIENT = 'info@yetudesignlab.com';
const SERVICE_LABELS: Record<string, string> = {
  branding: 'Branding & Identity',
  web: 'Web Design & Build',
  marketing: 'Marketing Campaigns',
  consulting: 'Creative Consulting',
  other: 'Other / Not Sure'
};
export function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState('');
  const [details, setDetails] = useState('');
  const buildMailto = () => {
    const serviceLabel = SERVICE_LABELS[service] ?? service;
    const subject = `Free consultation request — ${name || 'New enquiry'}`;
    const body = [`Name: ${name}`, `Email: ${email}`, `Service of interest: ${serviceLabel}`, '', 'Project details:', details].join('\n');
    return `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    // Hand the pre-filled draft to the visitor's email client — no backend,
    // no account, delivered straight to info@yetudesignlab.com.
    window.location.href = buildMailto();
    setTimeout(() => {
      setStatus('success');
    }, 600);
  };
  const resetForm = () => {
    setName('');
    setEmail('');
    setService('');
    setDetails('');
    setStatus('idle');
  };
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-raise-2 border border-line-2 rounded-r-lg p-8 text-center" data-id="element-36">
        <div className="w-16 h-16 bg-lime/10 text-lime rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-37">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-38">
            <polyline points="20 6 9 17 4 12" data-id="element-39"></polyline>
          </svg>
        </div>
        <h3 className="text-2xl font-bold mb-2" data-id="element-40">Almost there</h3>
        <p className="text-mut mb-6" data-id="element-41">
          Your email app should have opened with your details ready to send to{' '}
          <span className="text-ink" data-id="element-42">{RECIPIENT}</span>. Just hit send and we'll
          get back to you within 24 hours.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-43">
          <a href={buildMailto()} className="ghost justify-center" data-id="element-44">
            Re-open email draft
          </a>
          <button onClick={resetForm} className="ghost justify-center" data-id="element-45">
            Send another message
          </button>
        </div>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="bg-raise-2 border border-line-2 rounded-r-lg p-6 md:p-8 text-left" data-id="element-46">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-47">
        <div data-id="element-48">
          <label htmlFor="name" className="block text-sm font-mono text-mut-2 uppercase tracking-wider mb-2" data-id="element-49">
            Name
          </label>
          <input type="text" id="name" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-raise border border-line-2 rounded-md px-4 py-3 text-ink placeholder:text-mut-2 focus:outline-none focus:border-lime focus:ring-2 focus:ring-lime/30 transition-colors" placeholder="Jane Doe" data-id="element-50" />
        </div>
        <div data-id="element-51">
          <label htmlFor="email" className="block text-sm font-mono text-mut-2 uppercase tracking-wider mb-2" data-id="element-52">
            Email
          </label>
          <input type="email" id="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-canvas border border-line rounded-md px-4 py-3 text-ink focus:outline-none focus:border-lime transition-colors" placeholder="jane@company.com" data-id="element-53" />
        </div>
      </div>

      <div className="mb-6" data-id="element-54">
        <label htmlFor="service" className="block text-sm font-mono text-mut-2 uppercase tracking-wider mb-2" data-id="element-55">
          Service of Interest
        </label>
        <select id="service" required value={service} onChange={e => setService(e.target.value)} className="w-full bg-canvas border border-line rounded-md px-4 py-3 text-ink focus:outline-none focus:border-lime transition-colors appearance-none" data-id="element-56">
          <option value="" disabled data-id="element-57">
            Select a service...
          </option>
          <option value="branding" data-id="element-58">Branding & Identity</option>
          <option value="web" data-id="element-59">Web Design & Build</option>
          <option value="marketing" data-id="element-60">Marketing Campaigns</option>
          <option value="consulting" data-id="element-61">Creative Consulting</option>
          <option value="other" data-id="element-62">Other / Not Sure</option>
        </select>
      </div>

      <div className="mb-8" data-id="element-63">
        <label htmlFor="details" className="block text-sm font-mono text-mut-2 uppercase tracking-wider mb-2" data-id="element-64">
          Project Details
        </label>
        <textarea id="details" required rows={4} value={details} onChange={e => setDetails(e.target.value)} className="w-full bg-canvas border border-line rounded-md px-4 py-3 text-ink focus:outline-none focus:border-lime transition-colors resize-none" placeholder="Tell us a bit about your goals, timeline, and budget..." data-id="element-65"></textarea>
      </div>

      <CtaButton as="button" type="submit" disabled={status === 'submitting'} className="w-full justify-center py-4" data-id="element-66">
        {status === 'submitting' ? 'Opening email...' : 'Book Free Consultation'}
      </CtaButton>
    </form>;
}